import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineClock, HiOutlineRefresh, HiOutlineCheckCircle,
  HiOutlineXCircle, HiOutlinePause, HiOutlinePlay, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDownload,
} from 'react-icons/hi';
import { getSchedulerJobs, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'scheduler';
const SCHEDULER_COST_INR = 9.10;

function formatCost(cost) {
  if (cost === null || cost === undefined) return '—';
  if (cost === 0) return <span className="cost-cell"><span className="cost-approx">~</span> ₹0</span>;
  return <span className="cost-cell"><span className="cost-approx">~</span> ₹{cost.toLocaleString('en-IN')}</span>;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function stateIcon(state) {
  if (state === 'ENABLED' || state === 1) return <HiOutlinePlay size={14} className="state-icon enabled" />;
  if (state === 'PAUSED' || state === 2) return <HiOutlinePause size={14} className="state-icon paused" />;
  if (state === 'DISABLED' || state === 3) return <HiOutlineXCircle size={14} className="state-icon disabled" />;
  return null;
}

function stateLabel(state) {
  if (state === 'ENABLED' || state === 1) return 'ENABLED';
  if (state === 'PAUSED' || state === 2) return 'PAUSED';
  if (state === 'DISABLED' || state === 3) return 'DISABLED';
  return String(state || 'UNKNOWN');
}

function statusBadge(status) {
  if (!status) return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> OK</span>;
  if (status.code === 0) return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> OK</span>;
  return <span className="status-badge error"><HiOutlineXCircle size={12} /> {status.message || `Code ${status.code}`}</span>;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function CloudScheduler({ gcpProjects, selectedProjectId }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchJobs = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getSchedulerJobs(activeProject._id);
      if (!signal?.cancelled) setJobs(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load scheduler jobs: ' + (err.response?.data?.error || err.message));
        setJobs([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  useEffect(() => {
    setJobs([]);
    setExpandedRows({});
    setTagMap({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchJobs(signal);
    fetchTags();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchJobs, fetchTags]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const sortFn = useCallback((list) => {
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, []);

  const grouped = useMemo(() => {
    let list = jobs;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(j =>
        j.name.toLowerCase().includes(q) ||
        j.region.toLowerCase().includes(q) ||
        stateLabel(j.state).toLowerCase().includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [jobs, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 10;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Job Name', 'Region', 'Schedule', 'State', 'Est. Cost (INR)', 'Last Run', 'Last Status', 'Next Run'];
    const rows = flatFiltered.map(j => [
      escapeCsvField(getTagLabel(tagMap[j.name]) || ''),
      escapeCsvField(j.name),
      escapeCsvField(j.region),
      escapeCsvField(j.schedule),
      escapeCsvField(stateLabel(j.state)),
      escapeCsvField(SCHEDULER_COST_INR),
      escapeCsvField(j.lastAttemptTime || ''),
      escapeCsvField(j.status?.code === 0 || !j.status ? 'OK' : (j.status.message || `Code ${j.status.code}`)),
      escapeCsvField(j.nextRunTime || ''),
    ]);

    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-scheduler-jobs-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineClock size={28} /> Cloud Scheduler</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view Cloud Scheduler jobs.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineClock size={28} /> Cloud Scheduler</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} job{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchJobs({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && jobs.length === 0 && (
        <div className="empty-state"><p>No scheduler jobs found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th style={stickyTh[2]}>Job Name</th>
                <th>Region</th>
                <th>Schedule</th>
                <th>State</th>
                <th>Est. Cost</th>
                <th>Last Run</th>
                <th>Last Status</th>
                <th>Next Run</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const j = row;
                const key = j.fullName || i;
                const isOpen = expandedRows[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[j.name] || ''} onChange={(tag) => handleTagChange(j.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>{j.name}</td>
                      <td><span className="region-badge">{j.region}</span></td>
                      <td><code>{j.schedule}</code></td>
                      <td>{stateIcon(j.state)} {stateLabel(j.state)}</td>
                      <td>{formatCost(SCHEDULER_COST_INR)}</td>
                      <td>{formatTimestamp(j.lastAttemptTime)}</td>
                      <td>{statusBadge(j.status)}</td>
                      <td>{formatTimestamp(j.nextRunTime)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-item full-width">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value mono">{j.fullName || '—'}</span>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Schedule</span>
                                  <span className="detail-value mono">{j.schedule}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Timezone</span>
                                  <span className="detail-value">{j.timeZone || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">State</span>
                                  <span className="detail-value">{stateIcon(j.state)} {stateLabel(j.state)}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Target Type</span>
                                  <span className="detail-value">{j.target || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Last Attempt</span>
                                  <span className="detail-value">{formatTimestamp(j.lastAttemptTime)}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Next Run</span>
                                  <span className="detail-value">{formatTimestamp(j.nextRunTime)}</span>
                                </div>
                              </div>
                              {j.httpUri && (
                                <div className="detail-item full-width">
                                  <span className="detail-label">HTTP Target URL</span>
                                  <span className="detail-value mono">{j.httpUri}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && jobs.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No jobs match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default CloudScheduler;
